import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  Upload,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  Sparkles,
  ArrowRight,
  FileText,
  X,
  Cpu,
  Brain,
  Code2,
  Smartphone,
  Cloud,
  BarChart3,
  Palette,
  Database,
  Shield,
  Bot,
  User,
  Phone,
  GraduationCap,
  Globe,
  MoreHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/atoms/Button';

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

/* ─────────────────────────────────────────────
   Types
   ───────────────────────────────────────────── */

interface JobCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface Partner {
  id: string;
  name: string;
  description: string;
  logo_url: string | null;
  website: string | null;
  tags: string[];
}

/* ─────────────────────────────────────────────
   Data
   ───────────────────────────────────────────── */

const JOB_CATEGORIES: JobCategory[] = [
  { id: 'backend', label: 'Backend Developer', icon: <Database className="w-4 h-4" /> },
  { id: 'frontend', label: 'Frontend Developer', icon: <Code2 className="w-4 h-4" /> },
  { id: 'fullstack', label: 'Full Stack Developer', icon: <Code2 className="w-4 h-4" /> },
  { id: 'data-ml', label: 'Data / ML Engineer', icon: <Brain className="w-4 h-4" /> },
  { id: 'devops', label: 'DevOps / Cloud', icon: <Cloud className="w-4 h-4" /> },
  { id: 'mobile', label: 'Mobile Developer', icon: <Smartphone className="w-4 h-4" /> },
  { id: 'product', label: 'Product Manager', icon: <BarChart3 className="w-4 h-4" /> },
  { id: 'design', label: 'UX / UI Designer', icon: <Palette className="w-4 h-4" /> },
  { id: 'cybersecurity', label: 'Cybersecurity', icon: <Shield className="w-4 h-4" /> },
  { id: 'ai-engineer', label: 'AI Engineer', icon: <Bot className="w-4 h-4" /> },
  { id: 'other', label: 'Other', icon: <MoreHorizontal className="w-4 h-4" /> },
];

const BLOCKED_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'yahoo.com', 'yahoo.fr', 'yahoo.co.uk',
  'aol.com',
  'icloud.com', 'me.com', 'mac.com',
  'protonmail.com', 'proton.me',
  'zoho.com',
  'yandex.com', 'yandex.ru',
  'mail.com', 'email.com',
  'gmx.com', 'gmx.net',
  'tutanota.com', 'tuta.com',
  'fastmail.com',
  'hey.com',
  'mailfence.com',
  'disroot.org',
  'riseup.net',
  'posteo.de',
  'inbox.com',
  'rediffmail.com',
]);

function isBlockedEmail(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase();
  return domain ? BLOCKED_EMAIL_DOMAINS.has(domain) : false;
}

/* ─────────────────────────────────────────────
   Animations
   ───────────────────────────────────────────── */

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay, ease: 'easeOut' as const },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08 },
  },
};

const pillVariant = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
};

/* ─────────────────────────────────────────────
   Partner Carousel (display-only, auto-scroll)
   ───────────────────────────────────────────── */

const PartnerCarousel: React.FC<{ partners: Partner[] }> = ({ partners }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || isPaused || partners.length === 0) return;

    let animId: number;
    let scrollPos = el.scrollLeft;

    const step = () => {
      scrollPos += 0.4;
      if (scrollPos >= el.scrollWidth / 2) {
        scrollPos = 0;
      }
      el.scrollLeft = scrollPos;
      animId = requestAnimationFrame(step);
    };
    animId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animId);
  }, [isPaused, partners.length]);

  // Duplicate for infinite scroll illusion
  const items = [...partners, ...partners];

  return (
    <div
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Fade edges */}
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-12 sm:w-24 z-10"
        style={{ background: 'linear-gradient(to right, #171D27, transparent)' }} />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 sm:w-24 z-10"
        style={{ background: 'linear-gradient(to left, #171D27, transparent)' }} />

      <div
        ref={scrollRef}
        className="flex gap-5 sm:gap-8 overflow-x-auto hide-scrollbar py-3"
      >
        {items.map((p, i) => (
          <div
            key={`${p.id}-${i}`}
            className="flex-shrink-0 flex flex-col items-center gap-3 w-48 sm:w-56"
          >
            {/* Logo or initial */}
            {p.logo_url ? (
              <img
                src={p.logo_url}
                alt={p.name}
                className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-xl bg-white/[0.04] p-2"
              />
            ) : (
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-white/[0.06] flex items-center justify-center">
                <Cpu className="w-6 h-6 text-white/30" />
              </div>
            )}
            <div className="text-center">
              <p className="text-white/70 text-sm font-semibold font-heading">{p.name}</p>
              <p className="text-white/35 text-xs leading-relaxed mt-1 line-clamp-2">{p.description}</p>
            </div>
            {/* Tags */}
            {p.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 justify-center">
                {p.tags.slice(0, 2).map((tag) => (
                  <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] text-white/25">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main Component
   ───────────────────────────────────────────── */

export const GitexChallengeLanding: React.FC = () => {
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [school, setSchool] = useState('');
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [otherRole, setOtherRole] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Progressive step: 1 = personal info, 2 = roles, 3 = resume + submit
  const [step, setStep] = useState(1);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Partners
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const step2Ref = useRef<HTMLDivElement>(null);
  const step3Ref = useRef<HTMLDivElement>(null);

  // Fetch partners from backend
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const res = await fetch(`${apiServerUrl}/api/challenge/partners`);
        if (res.ok) {
          const data: Partner[] = await res.json();
          setPartners(data);
        }
      } catch {
        // Silently fail
      } finally {
        setPartnersLoading(false);
      }
    };
    fetchPartners();
  }, []);

  const isValidEmail = !!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  const isEmailBlocked = isValidEmail && isBlockedEmail(email);
  const isStep1Valid = firstName.trim() !== '' && lastName.trim() !== '' && isValidEmail && !isEmailBlocked && country.trim() !== '' && mobileNumber.trim().length >= 4;

  const toggleJob = useCallback((id: string) => {
    setSelectedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCvFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.type === 'application/pdf' || file.name.endsWith('.pdf') || file.name.endsWith('.docx') || file.name.endsWith('.doc'))) {
      setCvFile(file);
    }
  };

  const advanceToStep2 = () => {
    if (!isStep1Valid) return;
    setStep(2);
    setTimeout(() => step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const advanceToStep3 = () => {
    setStep(3);
    setTimeout(() => step3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isStep1Valid || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Build roles list, replacing 'other' with the custom text
      const roles = Array.from(selectedJobs).map(r => r === 'other' && otherRole.trim() ? `other:${otherRole.trim()}` : r);

      const formData = new FormData();
      formData.append('first_name', firstName.trim());
      formData.append('last_name', lastName.trim());
      formData.append('email', email.trim().toLowerCase());
      formData.append('country', country.trim());
      formData.append('mobile_number', mobileNumber.trim());
      if (school.trim()) formData.append('school', school.trim());
      formData.append('selected_roles', roles.join(','));
      // Honeypot -- must be empty
      formData.append('website', '');
      if (cvFile) {
        formData.append('cv', cvFile);
      }

      const res = await fetch(`${apiServerUrl}/api/challenge/register`, {
        method: 'POST',
        body: formData,
      });

      if (res.status === 429) {
        setSubmitError('Too many registrations from your network. Please try again later.');
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setSubmitError(data?.detail || 'Registration failed. Please try again.');
        return;
      }

      setIsSubmitted(true);
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="min-h-screen bg-[#171D27] text-white overflow-x-hidden">
      {/* ── Background ambient glows ── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(circle, #FAC8D2 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(circle, #E5CBF6 0%, transparent 70%)' }} />
        <div className="absolute top-[40%] left-[50%] w-[40%] h-[40%] rounded-full opacity-[0.04] translate-x-[-50%]"
          style={{ background: 'radial-gradient(circle, #D9ADFF 0%, transparent 70%)' }} />
      </div>

      {/* ════════════════════════════════════
          HEADER
         ════════════════════════════════════ */}
      <header className="relative z-20">
        <nav className="flex justify-between items-center px-4 sm:px-6 lg:px-12 h-16 sm:h-20">
          <div className="flex items-center gap-2">
            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              src="/logo/light-logo.png"
              alt="Invirtus"
              className="w-8 h-8 lg:w-10 lg:h-10"
            />
            <span className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold font-heading">
              Invirtus
            </span>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex items-center gap-3 sm:gap-4"
          >
            <img
              src="/logo/gitex-africa.svg"
              alt="GITEX Africa"
              className="hidden sm:block h-10 lg:h-14 object-contain"
            />
            <Button
              type="contained"
              intent="primary"
              size="sm"
              label="Register"
              onClick={scrollToForm}
            />
          </motion.div>
        </nav>
      </header>

      {/* ════════════════════════════════════
          HERO SECTION
         ════════════════════════════════════ */}
      <section className="relative z-10 px-4 sm:px-6 lg:px-12 pt-8 sm:pt-16 lg:pt-20 pb-16 sm:pb-24">
        {/* Dot pattern overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            background: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.5) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />

        <div className="max-w-5xl mx-auto text-center relative">
          {/* GITEX Africa logo + Coming Soon badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center gap-4 mb-6 sm:mb-8"
          >
            <img
              src="/logo/gitex-africa.svg"
              alt="GITEX Africa 2026"
              className="h-18 sm:h-22 lg:h-28 object-contain"
            />
            <div className="flex items-center gap-3">
              <div className="h-px w-8 sm:w-12 bg-white/10" />
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D9ADFF]/30 bg-[#D9ADFF]/10">
                <Sparkles className="w-4 h-4 text-[#D9ADFF]" />
                <span className="text-[#D9ADFF] text-xs sm:text-sm font-semibold tracking-wide uppercase">
                  Coming Soon
                </span>
              </div>
              <div className="h-px w-8 sm:w-12 bg-white/10" />
            </div>
          </motion.div>

          {/* Main headline */}
          <motion.h1
            custom={0.1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.08] tracking-tight mb-6"
          >
            <span className="text-white">The AI Challenge</span>
            <br />
            <span className="text-[#D9ADFF]">that recruits top</span>
            <br />
            <span className="text-white">tech talent.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            custom={0.25}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="text-white/60 text-base sm:text-lg lg:text-xl max-w-2xl mx-auto mb-8 sm:mb-10 font-medium leading-relaxed"
          >
            Take AI-powered interviews, prove your skills, and
            <br className="hidden sm:block" />
            <span className="text-white/80 font-semibold">land your dream job with top tech startups.</span>
          </motion.p>

          {/* CTA */}
          <motion.div
            custom={0.4}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center"
          >
            <Button
              type="contained"
              intent="primary"
              size="md"
              label="Join the Challenge"
              rightIcon={<ArrowRight />}
              onClick={scrollToForm}
            />
            <a
              href="#how-it-works"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-white/50 hover:text-white/80 text-sm font-medium transition-colors flex items-center gap-1"
            >
              How it works
              <ChevronRight className="w-4 h-4" />
            </a>
          </motion.div>

          {/* Early registration incentive */}
          <motion.div
            custom={0.55}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mt-8 sm:mt-10 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#FAC8D2]/20 bg-[#FAC8D2]/[0.06]"
          >
            <Sparkles className="w-4 h-4 text-[#FAC8D2]" />
            <span className="text-white/70 text-sm font-medium">
              Register early and receive an exclusive <strong className="text-[#FAC8D2]">invitation badge</strong> to GITEX Africa
            </span>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════
          HOW IT WORKS
         ════════════════════════════════════ */}
      <section id="how-it-works" className="relative z-10 px-4 sm:px-6 lg:px-12 py-16 sm:py-24">
        <div className="max-w-5xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-4">
              How it works
            </h2>
            <p className="text-white/50 text-base sm:text-lg max-w-xl mx-auto">
              3 simple steps to access the best tech opportunities.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                step: '01',
                title: 'Sign up',
                desc: 'Fill in your details, pick the roles you\'re interested in, and optionally upload your resume.',
                color: '#FAC8D2',
              },
              {
                step: '02',
                title: 'Take the AI Challenge',
                desc: 'After GITEX, receive your personalized AI interview link. A unique experience designed to reveal what makes you stand out.',
                color: '#D9ADFF',
              },
              {
                step: '03',
                title: 'Land your interview',
                desc: 'Top performers are introduced to our partner startups for exclusive interview opportunities.',
                color: '#C0E1FF',
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                custom={i * 0.15}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                className="relative group"
              >
                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm p-6 sm:p-8 h-full hover:border-white/[0.15] transition-all duration-300 hover:bg-white/[0.05]">
                  <span
                    className="text-5xl sm:text-6xl font-heading font-extrabold opacity-[0.08] absolute top-4 right-6"
                    style={{ color: item.color }}
                  >
                    {item.step}
                  </span>
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <span className="text-lg font-bold" style={{ color: item.color }}>
                      {item.step}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-lg sm:text-xl text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-white/50 text-sm sm:text-base leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          PARTNER STARTUPS (display-only carousel)
         ════════════════════════════════════ */}
      {!partnersLoading && partners.length > 0 && (
        <section className="relative z-10 px-4 sm:px-6 lg:px-12 py-16 sm:py-24">
          <div className="max-w-5xl mx-auto">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              className="text-center mb-10 sm:mb-14"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#C0E1FF]/20 bg-[#C0E1FF]/5 mb-4">
                <span className="text-[#C0E1FF] text-xs font-semibold tracking-wide uppercase">
                  Edge AI Applications
                </span>
              </div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-4">
                Partner Startups
              </h2>
              <p className="text-white/50 text-base sm:text-lg max-w-xl mx-auto">
                Leading startups recruiting top talent through the Invirtus Challenge.
              </p>
            </motion.div>

            <PartnerCarousel partners={partners} />
          </div>
        </section>
      )}

      {/* ════════════════════════════════════
          PROGRESSIVE REGISTRATION FORM
          Step 1: Email → Step 2: Roles → Step 3: Resume + Submit
         ════════════════════════════════════ */}
      <section ref={formRef} className="relative z-10 px-4 sm:px-6 lg:px-12 py-16 sm:py-24">
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            {isSubmitted ? (
              /* ── Success state ── */
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="text-center py-12 sm:py-16"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#5DBE7A]/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-[#5DBE7A]" />
                </div>
                <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-3">
                  You're registered!
                </h2>
                <p className="text-white/50 text-base sm:text-lg max-w-md mx-auto mb-6">
                  You'll receive an email with next steps after GITEX Africa.
                  Get ready for the Challenge!
                </p>
                <Button
                  type="text"
                  intent="primary"
                  size="sm"
                  label="Register another candidate"
                  onClick={() => {
                    setIsSubmitted(false);
                    setFirstName('');
                    setLastName('');
                    setEmail('');
                    setCountry('');
                    setMobileNumber('');
                    setSchool('');
                    setCvFile(null);
                    setSelectedJobs(new Set());
                    setOtherRole('');
                    setStep(1);
                    setSubmitError(null);
                  }}
                />
              </motion.div>
            ) : (
              /* ── Progressive form ── */
              <motion.div
                key="form"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
              >
                <div className="text-center mb-10 sm:mb-12">
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-4">
                    Join the Challenge
                  </h2>
                  <p className="text-white/50 text-base sm:text-lg max-w-lg mx-auto">
                    Register in a few seconds. We'll reach out with your AI interview link after GITEX.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                  {/* Honeypot -- hidden from real users, bots fill it */}
                  <div className="absolute opacity-0 pointer-events-none" aria-hidden="true" tabIndex={-1}>
                    <input name="website" type="text" autoComplete="off" tabIndex={-1} />
                  </div>

                  {/* ── STEP 1: Personal Information ── */}
                  <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm p-5 sm:p-8">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-8 h-8 rounded-full bg-[#FAC8D2]/15 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-[#FAC8D2]">1</span>
                      </div>
                      <h3 className="font-heading font-bold text-lg text-white">Your information</h3>
                    </div>

                    <div className="space-y-4">
                      {/* First name + Last name row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                          <input
                            type="text"
                            required
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="First name"
                            className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.05] border border-white/10 text-white placeholder-white/30 focus:border-[#D9ADFF]/50 focus:ring-1 focus:ring-[#D9ADFF]/30 focus:outline-none transition-all text-sm sm:text-base"
                          />
                        </div>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                          <input
                            type="text"
                            required
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Last name"
                            className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.05] border border-white/10 text-white placeholder-white/30 focus:border-[#D9ADFF]/50 focus:ring-1 focus:ring-[#D9ADFF]/30 focus:outline-none transition-all text-sm sm:text-base"
                          />
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                          <input
                            id="email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Work or university email"
                            className={`w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.05] border text-white placeholder-white/30 focus:outline-none transition-all text-sm sm:text-base ${
                              isEmailBlocked
                                ? 'border-[#E85D5D]/50 focus:border-[#E85D5D]/70 focus:ring-1 focus:ring-[#E85D5D]/30'
                                : 'border-white/10 focus:border-[#D9ADFF]/50 focus:ring-1 focus:ring-[#D9ADFF]/30'
                            }`}
                          />
                        </div>
                        {isEmailBlocked && (
                          <p className="text-[#E85D5D] text-xs mt-1.5 ml-1">
                            Please use a work or university email (not Gmail, Outlook, etc.)
                          </p>
                        )}
                      </div>

                      {/* Country + Mobile row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="relative">
                          <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                          <input
                            type="text"
                            required
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            placeholder="Country"
                            className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.05] border border-white/10 text-white placeholder-white/30 focus:border-[#D9ADFF]/50 focus:ring-1 focus:ring-[#D9ADFF]/30 focus:outline-none transition-all text-sm sm:text-base"
                          />
                        </div>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                          <input
                            type="tel"
                            required
                            value={mobileNumber}
                            onChange={(e) => setMobileNumber(e.target.value)}
                            placeholder="Mobile number"
                            className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.05] border border-white/10 text-white placeholder-white/30 focus:border-[#D9ADFF]/50 focus:ring-1 focus:ring-[#D9ADFF]/30 focus:outline-none transition-all text-sm sm:text-base"
                          />
                        </div>
                      </div>

                      {/* School (optional) */}
                      <div className="relative">
                        <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                        <input
                          type="text"
                          value={school}
                          onChange={(e) => setSchool(e.target.value)}
                          placeholder="School / University (optional)"
                          className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/[0.05] border border-white/10 text-white placeholder-white/30 focus:border-[#D9ADFF]/50 focus:ring-1 focus:ring-[#D9ADFF]/30 focus:outline-none transition-all text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    {step === 1 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-5"
                      >
                        <Button
                          type="contained"
                          intent="primary"
                          size="md"
                          label="Continue"
                          rightIcon={<ChevronDown />}
                          onClick={advanceToStep2}
                          disabled={!isStep1Valid}
                          className="w-full"
                        />
                      </motion.div>
                    )}
                  </div>

                  {/* ── STEP 2: Role selection ── */}
                  <AnimatePresence>
                    {step >= 2 && (
                      <motion.div
                        ref={step2Ref}
                        key="step2"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm p-5 sm:p-8"
                      >
                        <div className="flex items-center gap-3 mb-5">
                          <div className="w-8 h-8 rounded-full bg-[#D9ADFF]/15 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-[#D9ADFF]">2</span>
                          </div>
                          <h3 className="font-heading font-bold text-lg text-white">Roles I'm interested in</h3>
                          <span className="text-white/30 text-xs ml-auto">{selectedJobs.size} selected</span>
                        </div>

                        <motion.div
                          variants={staggerContainer}
                          initial="hidden"
                          animate="visible"
                          className="flex flex-wrap gap-2.5 sm:gap-3"
                        >
                          {JOB_CATEGORIES.map((job) => {
                            const isSelected = selectedJobs.has(job.id);
                            return (
                              <motion.button
                                key={job.id}
                                type="button"
                                variants={pillVariant}
                                onClick={() => toggleJob(job.id)}
                                className={`
                                  flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium
                                  border transition-all duration-200 cursor-pointer
                                  ${isSelected
                                    ? 'border-[#D9ADFF] bg-[#D9ADFF]/15 text-[#D9ADFF] shadow-[0_0_12px_rgba(217,173,255,0.1)]'
                                    : 'border-white/10 bg-white/[0.03] text-white/60 hover:border-white/20 hover:text-white/80'
                                  }
                                `}
                              >
                                {job.icon}
                                {job.label}
                                {isSelected && <CheckCircle2 className="w-3.5 h-3.5 ml-0.5" />}
                              </motion.button>
                            );
                          })}
                        </motion.div>

                        {/* "Other" role text input */}
                        <AnimatePresence>
                          {selectedJobs.has('other') && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="mt-3 overflow-hidden"
                            >
                              <input
                                type="text"
                                value={otherRole}
                                onChange={(e) => setOtherRole(e.target.value)}
                                placeholder="Please specify your role"
                                className="w-full px-4 py-3 rounded-xl bg-white/[0.05] border border-white/10 text-white placeholder-white/30 focus:border-[#D9ADFF]/50 focus:ring-1 focus:ring-[#D9ADFF]/30 focus:outline-none transition-all text-sm"
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {step === 2 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="mt-5"
                          >
                            <Button
                              type="contained"
                              intent="primary"
                              size="md"
                              label="Continue"
                              rightIcon={<ChevronDown />}
                              onClick={advanceToStep3}
                              className="w-full"
                            />
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* ── STEP 3: Resume (optional) + Submit ── */}
                  <AnimatePresence>
                    {step >= 3 && (
                      <motion.div
                        ref={step3Ref}
                        key="step3"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm p-5 sm:p-8"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 rounded-full bg-[#C0E1FF]/15 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-[#C0E1FF]">3</span>
                          </div>
                          <h3 className="font-heading font-bold text-lg text-white">Upload your resume</h3>
                          <span className="text-white/30 text-xs ml-auto">Optional</span>
                        </div>
                        <p className="text-white/35 text-xs mb-5 ml-11">
                          Don't have it handy? No worries, we'll email you a link to upload it later.
                        </p>

                        {cvFile ? (
                          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#D9ADFF]/10 border border-[#D9ADFF]/20">
                            <FileText className="w-5 h-5 text-[#D9ADFF] flex-shrink-0" />
                            <span className="text-white/80 text-sm truncate flex-1">{cvFile.name}</span>
                            <Button
                              type="icon"
                              intent="muted"
                              size="xs"
                              icon={<X />}
                              onClick={() => setCvFile(null)}
                              aria-label="Remove resume"
                            />
                          </div>
                        ) : (
                          <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={`
                              flex flex-col items-center justify-center gap-2 py-8 sm:py-10
                              rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200
                              ${isDragging
                                ? 'border-[#D9ADFF]/50 bg-[#D9ADFF]/5'
                                : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
                              }
                            `}
                          >
                            <Upload className={`w-6 h-6 ${isDragging ? 'text-[#D9ADFF]' : 'text-white/30'}`} />
                            <p className="text-white/40 text-sm text-center">
                              <span className="text-[#D9ADFF] font-medium">Click</span> or drag your resume here
                            </p>
                            <p className="text-white/20 text-xs">PDF, DOC, DOCX</p>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={handleFileChange}
                              className="hidden"
                            />
                          </div>
                        )}

                        {/* Error message */}
                        {submitError && (
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-[#E85D5D] text-sm mt-4 text-center"
                          >
                            {submitError}
                          </motion.p>
                        )}

                        {/* Submit */}
                        <div className="flex flex-col items-center gap-3 mt-6">
                          <Button
                            type="contained"
                            intent="primary"
                            size="md"
                            htmlType="submit"
                            disabled={!isStep1Valid || isSubmitting}
                            loading={isSubmitting}
                            label={
                              isSubmitting
                                ? 'Registering...'
                                : cvFile
                                  ? 'Register for the Challenge'
                                  : 'Register now, send my CV later'
                            }
                            icon={!isSubmitting && cvFile ? <ArrowRight /> : undefined}
                            className="w-full"
                          />
                          <p className="text-white/25 text-xs text-center">
                            {cvFile
                              ? 'By registering, you agree to receive communications from Invirtus.'
                              : 'We\'ll send you a confirmation email with a link to upload your resume anytime.'
                            }
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ════════════════════════════════════
          FOOTER
         ════════════════════════════════════ */}
      <footer className="relative z-10 border-t border-white/[0.06] px-4 sm:px-6 lg:px-12 py-8 sm:py-10">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo/light-logo.png" alt="Invirtus" className="w-6 h-6" />
            <span className="text-white/40 text-sm font-heading font-semibold">Invirtus</span>
            <span className="text-white/20 text-xs mx-1">x</span>
            <img src="/logo/gitex-africa.svg" alt="GITEX Africa" className="h-5 object-contain opacity-40" />
          </div>
          <div className="flex items-center gap-6">
            <a href="https://invirtus.co" target="_blank" rel="noopener noreferrer"
              className="text-white/30 hover:text-white/60 text-sm transition-colors">
              invirtus.co
            </a>
            <a href="/terms" className="text-white/30 hover:text-white/60 text-sm transition-colors">
              Terms
            </a>
            <a href="/privacy" className="text-white/30 hover:text-white/60 text-sm transition-colors">
              Privacy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
